/**
 * A single searchable settings field, mapping a user-facing label to the tab
 * it lives on and the DOM anchor to scroll to when selected.
 */
export interface SettingsSearchEntry {
  /** The `?tab=` value of the settings section this field lives in. */
  tab: string;
  /** Human-readable name of the settings section (breadcrumb prefix). */
  tabLabel: string;
  /** The field's user-facing label (breadcrumb leaf). */
  label: string;
  /** DOM id of the field's anchor element within the panel. */
  anchorId: string;
  /** Extra synonyms a user might type when looking for this field. */
  keywords?: string[];
}

/**
 * A settings search entry paired with the breadcrumb path shown to the user,
 * e.g. "Organization › Organization Name".
 */
export interface SettingsSearchResult extends SettingsSearchEntry {
  /** Breadcrumb path built from the tab label and field label. */
  path: string;
}

/**
 * Static index of searchable settings fields. Each entry's `anchorId` must match
 * an `id` attribute on the corresponding field in its panel component (usually a
 * `SettingsRow` or `SectionLabel`). Keep this in sync when adding settings.
 */
export const SETTINGS_SEARCH_INDEX: SettingsSearchEntry[] = [
  // Personal Settings
  {
    tab: "personal",
    tabLabel: "Personal Settings",
    label: "Profile",
    anchorId: "personal-profile",
    keywords: ["name", "display name", "avatar"],
  },
  {
    tab: "personal",
    tabLabel: "Personal Settings",
    label: "Email",
    anchorId: "personal-email",
    keywords: ["email address"],
  },
  {
    tab: "personal",
    tabLabel: "Personal Settings",
    label: "Theme",
    anchorId: "personal-theme",
    keywords: ["dark mode", "light mode", "appearance"],
  },
  {
    tab: "personal",
    tabLabel: "Personal Settings",
    label: "Daily report",
    anchorId: "personal-status-report",
    keywords: ["status report", "digest", "daily email", "report template"],
  },
  {
    tab: "personal",
    tabLabel: "Personal Settings",
    label: "Quote views",
    anchorId: "personal-quote-view-notifications",
    keywords: ["notifications", "quote viewed", "email alerts", "opt out"],
  },
  // Report Settings
  {
    tab: "report-settings",
    tabLabel: "Report Settings",
    label: "Standard: Your Tasks",
    anchorId: "report-template-standard",
    keywords: ["status report", "task report", "daily digest"],
  },
  {
    tab: "report-settings",
    tabLabel: "Report Settings",
    label: "Sales Rep: My Deals",
    anchorId: "report-template-sales_rep",
    keywords: ["status report", "follow up report", "sales report", "cadence"],
  },
  {
    tab: "report-settings",
    tabLabel: "Report Settings",
    label: "Estimator: My Queue",
    anchorId: "report-template-estimator",
    keywords: ["status report", "estimate queue", "work-up report"],
  },
  {
    tab: "report-settings",
    tabLabel: "Report Settings",
    label: "Manager: Deal Acceleration",
    anchorId: "report-template-manager",
    keywords: ["status report", "deal acceleration", "pipeline report"],
  },
  // Organization
  {
    tab: "organization",
    tabLabel: "Organization",
    label: "Organization Name",
    anchorId: "organization-name",
    keywords: ["company name", "org name"],
  },
  {
    tab: "organization",
    tabLabel: "Organization",
    label: "Organization Logo",
    anchorId: "organization-logo",
    keywords: ["logo", "brand", "upload logo"],
  },
  {
    tab: "organization",
    tabLabel: "Organization",
    label: "Standard Agent",
    anchorId: "organization-standard-agent",
    keywords: ["enable agent", "disable agent"],
  },
  {
    tab: "organization",
    tabLabel: "Organization",
    label: "Phone Number",
    anchorId: "organization-phone",
    keywords: ["contact number", "telephone"],
  },
  {
    tab: "organization",
    tabLabel: "Organization",
    label: "Fax Number",
    anchorId: "organization-fax",
    keywords: ["fax"],
  },
  {
    tab: "organization",
    tabLabel: "Organization",
    label: "Website",
    anchorId: "organization-website",
    keywords: ["url", "web address"],
  },
  {
    tab: "organization",
    tabLabel: "Organization",
    label: "Organization Address",
    anchorId: "organization-address",
    keywords: ["mailing address", "street", "location"],
  },
  {
    tab: "organization",
    tabLabel: "Organization",
    label: "Multi-Currency",
    anchorId: "organization-multi-currency",
    keywords: ["currency", "foreign currency"],
  },
  {
    tab: "organization",
    tabLabel: "Organization",
    label: "Base Currency",
    anchorId: "organization-base-currency",
    keywords: ["default currency", "usd"],
  },
  // Item Settings
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Item Custom Fields",
    anchorId: "item-defaults-custom-fields",
    keywords: ["custom fields", "item fields", "erp part metadata"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Margin Configuration",
    anchorId: "item-defaults-margin-configuration",
    keywords: ["default margin", "markup"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Yield Rate",
    anchorId: "item-defaults-yield-rate",
    keywords: ["yield", "scrap rate"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Maximum Part Number Length",
    anchorId: "item-defaults-advanced-max-part-number-length",
    keywords: ["advanced settings", "part number", "length", "erp"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Maximum Purchase Cost Breaks",
    anchorId: "item-defaults-advanced-max-purchase-cost-breaks",
    keywords: ["advanced settings", "purchase cost", "price breaks", "erp"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Allow Efficiency Adjustments",
    anchorId: "item-defaults-advanced-allow-operation-efficiency",
    keywords: ["advanced settings", "efficiency", "operations", "erp"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Allow Operation Quantity Breaks",
    anchorId: "item-defaults-advanced-allow-operation-quantity-breaks",
    keywords: ["advanced settings", "quantity breaks", "operations", "erp"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Allow Operation Quantity Per",
    anchorId: "item-defaults-advanced-allow-operation-qty-per",
    keywords: ["advanced settings", "qty per", "operations", "erp"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Allow Fixed Component Quantities",
    anchorId: "item-defaults-advanced-allow-component-fixed-quantity",
    keywords: ["advanced settings", "fixed quantity", "components", "erp"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Allow Component Scrap",
    anchorId: "item-defaults-advanced-allow-component-scrap",
    keywords: ["advanced settings", "scrap", "components", "erp"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Allow Component Cost Overrides",
    anchorId: "item-defaults-advanced-allow-component-cost-override",
    keywords: ["advanced settings", "cost override", "components", "erp"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Allow Component Costing Method Overrides",
    anchorId: "item-defaults-advanced-allow-component-costing-method-override",
    keywords: ["advanced settings", "costing method", "components", "erp"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Allow Component-to-Operation Links",
    anchorId: "item-defaults-advanced-allow-component-operation-links",
    keywords: ["advanced settings", "operation link", "components", "erp"],
  },
  {
    tab: "item-defaults",
    tabLabel: "Item Settings",
    label: "Allowed Units of Measure",
    anchorId: "item-defaults-advanced-allowed-units",
    keywords: ["advanced settings", "units", "uom", "erp"],
  },
  // Company Settings
  {
    tab: "company-settings",
    tabLabel: "Company Settings",
    label: "Company Custom Fields",
    anchorId: "company-settings-custom-fields",
    keywords: ["custom fields", "company fields", "customer fields"],
  },
  // Quote Settings
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Public Quote Template",
    anchorId: "quote-settings-public-template",
    keywords: ["template", "layout", "quote design"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Allow per line dimensions",
    anchorId: "quote-settings-item-field-overrides",
    keywords: [
      "override",
      "item field overrides",
      "line dimensions",
      "length",
      "width",
      "height",
      "weight",
      "dimension unit",
    ],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Require E-Signature",
    anchorId: "quote-settings-require-esignature",
    keywords: ["signature", "e-sign", "sign"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Quote Number Prefix",
    anchorId: "quote-settings-number-prefix",
    keywords: ["prefix", "quote number"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Terms and Conditions",
    anchorId: "quote-settings-terms-conditions",
    keywords: ["t&c", "legal terms"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Payment Terms",
    anchorId: "quote-settings-payment-terms",
    keywords: ["net 30", "deposit"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Delivery Terms",
    anchorId: "quote-settings-delivery-terms",
    keywords: ["fob", "shipping terms"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Quote Footer",
    anchorId: "quote-settings-footer",
    keywords: ["disclaimer", "footer text"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Require Location",
    anchorId: "quote-settings-advanced-require-location",
    keywords: ["advanced settings", "location", "required", "erp"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Require Ship-To",
    anchorId: "quote-settings-advanced-require-ship-to",
    keywords: [
      "advanced settings",
      "ship to",
      "shipping address",
      "required",
      "erp",
    ],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Require Contact",
    anchorId: "quote-settings-advanced-require-contact",
    keywords: ["advanced settings", "contact", "required"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Require Length and Width",
    anchorId: "quote-settings-advanced-require-length-and-width",
    keywords: [
      "advanced settings",
      "dimensions",
      "cut size",
      "width",
      "length",
      "weight",
      "erp",
    ],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Require an Item on Every Line",
    anchorId: "quote-settings-advanced-require-line-item",
    keywords: ["advanced settings", "item", "line", "required", "erp"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Maximum Quantity Breaks per Line",
    anchorId: "quote-settings-advanced-max-quantity-breaks",
    keywords: ["advanced settings", "quantity breaks", "price breaks", "erp"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Allow Fractional Quantities",
    anchorId: "quote-settings-advanced-allow-fractional-quantities",
    keywords: [
      "advanced settings",
      "decimal quantities",
      "whole numbers",
      "erp",
    ],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Maximum Quote Number Length",
    anchorId: "quote-settings-advanced-max-quote-number-length",
    keywords: ["advanced settings", "quote number", "length", "erp"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Maximum Delivery Terms Length",
    anchorId: "quote-settings-advanced-max-delivery-terms-length",
    keywords: ["advanced settings", "delivery terms", "length", "erp"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Allow Discount Lines",
    anchorId: "quote-settings-advanced-allow-discount-lines",
    keywords: ["advanced settings", "discount", "erp"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Allow Duplicate Items",
    anchorId: "quote-settings-advanced-allow-duplicate-items",
    keywords: ["advanced settings", "duplicate", "same item", "erp"],
  },
  {
    tab: "quote-settings",
    tabLabel: "Quote Settings",
    label: "Allow new ship-to addresses",
    anchorId: "quote-settings-advanced-allow-ship-to-creation",
    keywords: ["advanced settings", "ship to", "shipping address", "erp"],
  },
  // Opportunity Settings
  {
    tab: "opportunity-settings",
    tabLabel: "Opportunity Settings",
    label: "Default Win Probability",
    anchorId: "opportunity-settings-default-win-probability",
    keywords: ["probability", "win rate", "likelihood"],
  },
  {
    tab: "opportunity-settings",
    tabLabel: "Opportunity Settings",
    label: "Opportunity Naming Guidance",
    anchorId: "opportunity-settings-naming-guidance",
    keywords: ["naming", "deal name", "opportunity name"],
  },
  {
    tab: "opportunity-settings",
    tabLabel: "Opportunity Settings",
    label: "Opportunity Stages",
    anchorId: "opportunity-settings-stages",
    keywords: ["stages", "pipeline", "deal stages"],
  },
  // Agent Settings
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Agent Name",
    anchorId: "agent-settings-agent-name",
    keywords: ["assistant name", "bot name"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Timezone",
    anchorId: "agent-settings-timezone",
    keywords: ["time zone", "tz"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Internal Email Domains",
    anchorId: "agent-settings-internal-email-domains",
    keywords: ["internal senders", "domains"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Escalation Contacts",
    anchorId: "agent-settings-escalation-contacts",
    keywords: ["escalation", "fallback users"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Email",
    anchorId: "agent-settings-email",
    keywords: ["email provider", "gmail", "outlook", "smtp"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "SMS",
    anchorId: "agent-settings-sms",
    keywords: ["text messages", "phone", "outbound"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "External Follow Ups",
    anchorId: "agent-settings-external-follow-ups",
    keywords: ["follow-up", "customer follow up", "intervals"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "RFQ Extraction",
    anchorId: "agent-settings-rfq-extraction",
    keywords: ["rfq", "line item extraction", "pdf"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Item Creation Policy",
    anchorId: "agent-settings-item-creation-policy",
    keywords: ["item creation", "price model only"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Quote Finalization Reviewers",
    anchorId: "agent-settings-quote-reviewers",
    keywords: ["reviewers", "approvers"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Quote CC Emails",
    anchorId: "agent-settings-quote-cc-emails",
    keywords: ["cc", "carbon copy"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Quoting SOP",
    anchorId: "agent-settings-quoting-sop",
    keywords: ["standard operating procedure", "quoting workflow"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Item Naming Guidance",
    anchorId: "agent-settings-item-naming-guidance",
    keywords: ["part number", "naming convention"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Item Search Sub-Agent",
    anchorId: "agent-settings-item-search-subagent",
    keywords: ["catalog search", "item search"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Demand Forecast Enabled",
    anchorId: "agent-settings-demand-forecast-enabled",
    keywords: ["forecasting", "demand"],
  },
  {
    tab: "agent-settings",
    tabLabel: "Agent Settings",
    label: "Demand Forecast SOP",
    anchorId: "agent-settings-demand-forecast-sop",
    keywords: ["forecast procedure"],
  },
  // List / section-level anchors for table-style panels
  {
    tab: "ai-workers",
    tabLabel: "AI Workers",
    label: "Primary Agent",
    anchorId: "ai-workers-primary-agent",
    keywords: ["default agent", "sidebar agent"],
  },
  {
    tab: "erp",
    tabLabel: "ERP Integration",
    label: "Sync Jobs",
    anchorId: "erp-sync-jobs",
    keywords: ["erp", "integration", "sync", "netsuite", "connect provider"],
  },
  {
    tab: "email-integrations",
    tabLabel: "Email Integrations",
    label: "Microsoft Outlook",
    anchorId: "email-integrations-outlook",
    keywords: ["outlook", "mailbox", "connect email"],
  },
  {
    tab: "custom-skills",
    tabLabel: "Custom Skills",
    label: "Custom Skills",
    anchorId: "custom-skills-header",
    keywords: ["skills", "add skill", "tools"],
  },
  {
    tab: "phone-numbers",
    tabLabel: "Phone Numbers",
    label: "Your Phone Numbers",
    anchorId: "phone-numbers-list",
    keywords: ["phone", "sms number", "claim number"],
  },
  {
    tab: "price-models",
    tabLabel: "Price Models",
    label: "Price Models",
    anchorId: "price-models-header",
    keywords: ["pricing", "python script", "margins"],
  },
];

/**
 * Searches the settings index for entries matching a query, ranking exact and
 * prefix label matches above substring and keyword matches.
 * @param query - The raw search text typed by the user
 * @param limit - Maximum number of results to return (default 8)
 * @returns Matching entries with their breadcrumb path, best matches first
 */
export function searchSettings(
  query: string,
  limit = 8,
): SettingsSearchResult[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return [];
  }

  return SETTINGS_SEARCH_INDEX.map((entry) => ({
    entry,
    score: scoreEntry(entry, normalized),
  }))
    .filter((scored) => scored.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ entry }) => ({
      ...entry,
      path: `${entry.tabLabel} › ${entry.label}`,
    }));
}

/**
 * Scores a settings entry against a normalized query. Higher is a better match;
 * 0 means no match.
 */
function scoreEntry(entry: SettingsSearchEntry, normalized: string): number {
  const label = entry.label.toLowerCase();
  if (label === normalized) {
    return 100;
  }
  if (label.startsWith(normalized)) {
    return 80;
  }
  if (label.includes(normalized)) {
    return 60;
  }
  if (entry.tabLabel.toLowerCase().includes(normalized)) {
    return 40;
  }
  if (entry.keywords?.some((keyword) => keyword.includes(normalized))) {
    return 20;
  }
  return 0;
}
